import { Box, Button, Text } from '@mantine/core';
import { IconCalendar, IconMail, IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { SUPPORT_EMAIL } from '../constants/supportContacts';
import OnboardingBookingModal from './OnboardingBookingModal';

const OnboardingBookingButton = () => {
  const { domain } = useSelector((state) => state.domain);

  const [isOpen, setIsOpen] = useState(false);
  // The API records the booking server-side (see OnboardingBookingModal), so
  // this flag is a client-side UI hint only, not a second writer. It is kept
  // out of Redux deliberately: the shared `domain` slice is what
  // SettingsDomainPage seeds its edit form from and PUTs back on save, and
  // `onboardingSessionScheduledAt` is not among the fields that PUT strips -
  // writing a client-invented timestamp there would ride along on the next
  // unrelated domain save and overwrite whatever the API actually stored.
  // Local state flips this button (and the branch below it) over to
  // "Contact Support" immediately without waiting for the next in-domain
  // navigation to refetch the domain, without risking that write-back.
  const [justBooked, setJustBooked] = useState(false);
  // Set when the modal exhausts its outcome_unknown retries and the user then
  // closes it (see OnboardingBookingModal's `handleClose`/`onUnresolved`).
  // Whether that attempt landed server-side is unknown, so this button must
  // not go back to offering a fresh attempt - that would remount the modal
  // with a new idempotency key and a live picker, risking a double booking on
  // the same slot the ambiguous attempt may have already taken.
  const [unresolved, setUnresolved] = useState(false);

  // Fired by the modal only once the user dismisses its confirmation body
  // (see OnboardingBookingModal's `handleClose`), not the instant the booking
  // POST resolves - this same flip is what makes the branch below render
  // instead of the modal, so firing it any earlier would unmount the modal
  // before the user had a chance to see that confirmation.
  const handleBooked = () => setJustBooked(true);
  const handleUnresolved = () => setUnresolved(true);

  if (unresolved) {
    return (
      <Box display="inline-block" pr="md" data-tour="bookOnboardingSession">
        <Button variant="transparent" onClick={() => window.location.reload()} leftSection={<IconRefresh size={16} />}>
          <Text component="span" visibleFrom="md">
            Reload to check your booking
          </Text>
        </Button>
      </Box>
    );
  }

  if (justBooked || domain.onboardingSessionScheduledAt) {
    return (
      <Box display="inline-block" pr="md" data-tour="bookOnboardingSession">
        <Button
          variant="transparent"
          component="a"
          href={`mailto:${SUPPORT_EMAIL}`}
          leftSection={<IconMail size={16} />}
        >
          <Text component="span" visibleFrom="md">
            Contact Support
          </Text>
        </Button>
      </Box>
    );
  }

  return (
    <Box display="inline-block" pr="md" data-tour="bookOnboardingSession">
      <Button variant="transparent" onClick={() => setIsOpen(true)} leftSection={<IconCalendar size={16} />}>
        <Text component="span" visibleFrom="md">
          Book Onboarding Session
        </Text>
      </Button>
      {isOpen && (
        <OnboardingBookingModal
          onClose={() => setIsOpen(false)}
          onBooked={handleBooked}
          onUnresolved={handleUnresolved}
        />
      )}
    </Box>
  );
};

export default OnboardingBookingButton;
